import { apiClient } from '@/api/apiClient'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ORGANIZATION_USERS_QUERY_KEY } from '@/features/organization/hooks/users'

export enum DeclarantType {
  IMPORTER = 'IMPORTER',
  REPRESENTATIVE = 'REPRESENTATIVE'
}

type Declarant = {
  id: string
  name: string

  type?: DeclarantType
  identificationNumber?: string
  placeOfRegistration?: string

  street?: string
  street2?: string
  streetNumber?: string
  subDivision?: string
  country?: string
  postCode?: string
  poBox?: string
  city?: string

  createdAt: string
  updatedAt?: string

  organizationId: string
}

type DeclarantCreatePayload = {
  name: string
  type?: DeclarantType
  identificationNumber?: string
  placeOfRegistration?: string
  street?: string
  street2?: string
  streetNumber?: string
  subDivision?: string
  country?: string
  postCode?: string
  poBox?: string
  city?: string
}

type DeclarantUpdatePayload = Partial<DeclarantCreatePayload>

const DECLARANTS_QUERY_KEY = 'declarants'

export const useGetAllDeclarants = (organizationId: string) => {
  return useQuery({
    queryKey: [DECLARANTS_QUERY_KEY, organizationId],
    queryFn: async () => {
      const response = await apiClient.get<Declarant[]>(
        `/v2/organizations/${organizationId}/declarants`
      )
      return response.data
    }
  })
}

export const useGetDeclarantById = (organizationId: string, declarantId: string) => {
  return useQuery({
    queryKey: [DECLARANTS_QUERY_KEY, organizationId, declarantId],
    queryFn: async () => {
      const response = await apiClient.get<Declarant>(
        `/v2/organizations/${organizationId}/declarants/${declarantId}`
      )
      return response.data
    },
    enabled: !!organizationId && !!declarantId
  })
}

export const useCreateDeclarant = (organizationId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: [DECLARANTS_QUERY_KEY, organizationId, 'create'],
    mutationFn: async (payload: DeclarantCreatePayload) => {
      const response = await apiClient.post<Declarant>(
        `/v2/organizations/${organizationId}/declarants`,
        payload
      )
      return response.data
    },
    onSuccess: data => {
      queryClient.setQueryData<Declarant[]>([DECLARANTS_QUERY_KEY, organizationId], oldData => [
        ...(oldData || []),
        data
      ])
    }
  })
}

export const useUpdateDeclarant = (organizationId: string, declarantId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: [DECLARANTS_QUERY_KEY, organizationId, declarantId, 'update'],
    mutationFn: async (payload: DeclarantUpdatePayload) => {
      const response = await apiClient.patch<Declarant>(
        `/v2/organizations/${organizationId}/declarants/${declarantId}`,
        payload
      )

      queryClient.setQueryData<Declarant[]>(
        [DECLARANTS_QUERY_KEY, organizationId],
        oldData =>
          oldData?.map(declarant => (declarant.id === declarantId ? response.data : declarant)) ||
          []
      )

      return response.data
    }
  })
}

export const useAssignDeclarantToUser = (organizationId: string, declarantId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: [DECLARANTS_QUERY_KEY, organizationId, declarantId, 'assign'],
    mutationFn: async (payload: { userId: string }) => {
      const response = await apiClient.post<{}>(
        `/v2/organizations/${organizationId}/declarants/${declarantId}/assign`,
        payload
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ORGANIZATION_USERS_QUERY_KEY, organizationId] })
    }
  })
}

export const useUnassignDeclarantFromUser = (
  organizationId: string,
  declarantId: string,
  userId: string
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: [DECLARANTS_QUERY_KEY, organizationId, declarantId, 'unassign', userId],
    mutationFn: async () => {
      const response = await apiClient.delete<{}>(
        `/v2/organizations/${organizationId}/declarants/${declarantId}/assign/${userId}`
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ORGANIZATION_USERS_QUERY_KEY, organizationId] })
    }
  })
}
